import React, { useMemo, createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE = 'bg_customer_auth';

const CustomerAuthCtx = createContext(null);

export const CustomerAuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE) || 'null'); } catch { return null; }
  });
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authReason, setAuthReason] = useState('');
  const onLoginRef = useRef(null);

  const setSession = (token, email) => {
    const s = { token, email };
    setAuth(s);
    localStorage.setItem(STORAGE, JSON.stringify(s));
    // Trigger the callback registered by whoever opened the modal
    if (onLoginRef.current) { try { onLoginRef.current(); } catch {} onLoginRef.current = null; }
  };

  /** Opens the sign-in/up modal; optionally runs a callback after successful auth. */
  const openAuth = (onSuccess, reason = '') => {
    onLoginRef.current = typeof onSuccess === 'function' ? onSuccess : null;
    setAuthReason(reason);
    setAuthModalOpen(true);
  };
  const closeAuth = () => { setAuthModalOpen(false); setAuthReason(''); onLoginRef.current = null; };

  const logout = () => {
    setAuth(null);
    localStorage.removeItem(STORAGE);
  };

  const signup = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/signup`, { email, password });
    setSession(data.token, data.email);
    return data;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setSession(data.token, data.email);
    return data;
  }, []);

  // Validate token on mount (silent)
  useEffect(() => {
    if (!auth?.token) return;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${auth.token}` } })
      .catch(() => logout());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headers = useMemo(() => (auth?.token ? { Authorization: `Bearer ${auth.token}` } : {}), [auth?.token]);

  return (
    <CustomerAuthCtx.Provider value={{
      auth, isAuthed: !!auth?.token, headers,
      signup, login, logout,
      authModalOpen, openAuth, closeAuth, authReason,
    }}>
      {children}
    </CustomerAuthCtx.Provider>
  );
};

export const useCustomerAuth = () => useContext(CustomerAuthCtx);
